/**
 * ListSkeleton Component
 * 
 * Reusable component for rendering skeleton lists.
 * Replaces manual [...Array(n)].map() patterns with a consistent interface.
 * 
 * @example
 * <ListSkeleton count={5} skeleton={PublicationCardSkeleton} />
 * <ListSkeleton count={3} skeleton={TableRowSkeleton} skeletonProps={{ columns: 5 }} gap="sm" />
 */

import { cn } from "@/lib/utils";
import { SPACING } from "@/design-system";
import { ComponentType } from "react";

type GapSize = 'xs' | 'sm' | 'md' | 'lg';

interface ListSkeletonProps<T extends Record<string, unknown> = Record<string, unknown>> {
  /** Number of skeleton items to render */
  count?: number;
  /** Skeleton component to render */
  skeleton: ComponentType<T>;
  /** Props to pass to each skeleton instance */
  skeletonProps?: T;
  /** Container className */
  className?: string;
  /** Gap between items */
  gap?: GapSize;
}

const gapClasses: Record<GapSize, string> = {
  xs: 'space-y-1',
  sm: 'space-y-2',
  md: SPACING.listItemGap,
  lg: SPACING.contentContainer,
};

export function ListSkeleton<T extends Record<string, unknown> = Record<string, unknown>>({ 
  count = 5, 
  skeleton: SkeletonComponent,
  skeletonProps = {} as T,
  className,
  gap = 'md',
}: ListSkeletonProps<T>) {
  return (
    <div className={cn(gapClasses[gap], className)}>
      {[...Array(count)].map((_, i) => (
        <SkeletonComponent key={i} {...skeletonProps} />
      ))}
    </div>
  );
}
